import { type SalesforceCallDisposition } from 'tendril-v2/src/Data/DataSource/sync/salesforce-mappings/dto/salesforce-custom-dispositions.dto';
import { type MutableRefObject, useContext, useEffect, useRef, useState } from 'react';
import { UserProviderContext } from 'tendril-v1/src/components/app/UserProvider';
import { type SalesforceAccount } from 'tendril-v2/src/Data/DataSource/sync/salesforce/dto/get-salesforcer-accounts.dto';
import getSalesforceAccountsAsync from 'tendril-v1/src/api/Sync/getSalesforcesAccountsAsync';
import {
  type Disposition,
  DispositionAbbreviation,
  DispositionAbbreviations,
  DispositionDefaultAction,
  type DispositionMap,
  type DispositionRecord,
  Outcome,
  TendrilDefaultActions,
} from 'tendril-v2/src/Presentation/Views/types/disposition-mapping.type';
import { getSalesforceMapping } from 'tendril-v2/src/Data/DataSource/sync/salesforce-mappings/getSalesforceMapping';
import type { SaveHubspotMappingDto } from 'tendril-v2/src/Data/DataSource/sync/hubspot-mappings/dto/save-hubspot-mapping.dto';
import { updateSalesforceMapping } from 'tendril-v2/src/Data/DataSource/sync/salesforce-mappings/updateSalesforceMapping';
import { toast } from 'tendril-v2/src/Presentation/Common/Components/ui/use-toast';
import { getSalesforceDispositions } from 'tendril-v2/src/Data/DataSource/sync/salesforce-mappings/getSalesforceDispositions';
import { LoadingState } from 'tendril-v1/src/types/LoadingStates.type';

export interface SalesforceMappingsViewmodel {
  fetchMappings: () => Promise<void>;
  fetchCallDispositions: () => Promise<SalesforceCallDisposition[]>;
  saveNewDispositionMap: () => Promise<void>;
  selectNewDisposition: (selected: Disposition, customDisposition: SalesforceCallDisposition) => void;
  fetchAccounts: () => Promise<SalesforceAccount[]>;
  salesforceAccounts: SalesforceAccount[];
  mappings: Disposition[];
  customDispositions: SalesforceCallDisposition[];
  loadingState: SalesforceMappingsLoadingState;
  viewState: ViewState;
  mappingsHaveChanged: MutableRefObject<boolean>;
}

interface SalesforceMappingsLoadingState {
  getMappings: LoadingState;
  getCallDispositions: LoadingState;
  saveMappings: LoadingState;
  getAccounts: LoadingState;
}

interface ViewState {
  isSalesforceAccount: boolean;
  isSuperAdmin: boolean;
  isAdmin: boolean;
  isTableLoading: boolean;
}

const { IDLE, LOADING, ERROR, SUCCESS } = LoadingState;

export function SalesforceMappingsViewModel(): SalesforceMappingsViewmodel {
  const {
    currentUser: { userId, organizationId, isAdmin, isSuperAdmin },
  } = useContext(UserProviderContext);
  const [salesforceAccounts, setSalesforceAccounts] = useState<SalesforceAccount[]>([]);

  const [mappings, setMappings] = useState<Disposition[]>([]);

  const [customDispositions, setCustomDispositions] = useState<SalesforceCallDisposition[]>([]);

  const [loadingState, setLoadingState] = useState<SalesforceMappingsLoadingState>({
    getMappings: IDLE,
    getCallDispositions: IDLE,
    saveMappings: IDLE,
    getAccounts: IDLE,
  });

  const [viewState, setViewState] = useState<ViewState>({
    isSalesforceAccount: false,
    isSuperAdmin,
    isAdmin,
    isTableLoading: false,
  });

  const mappingsHaveChanged = useRef(false);

  function isCustomDisposition(customName: string): boolean {
    return (
      // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison
      customName !== TendrilDefaultActions.default_action &&
      // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison
      Object.values(TendrilDefaultActions).every((tendrilDefaultName) => customName !== tendrilDefaultName)
    );
  }

  function transformMappings(
    mappings: DispositionRecord,
    callDispositions: SalesforceCallDisposition[],
    dispositionId: string,
  ): Disposition[] {
    const MAX_DISPOSITIONS = 16;
    const transformedMappings: Disposition[] = [];

    for (let index = 0; index < MAX_DISPOSITIONS; index++) {
      const isDefault = index === MAX_DISPOSITIONS - 1;
      const abbreviation = DispositionAbbreviations[index];
      const defaultAction = DispositionDefaultAction[abbreviation];
      const defaultName = TendrilDefaultActions[abbreviation];
      const mappingName = isDefault ? 'default_action' : mappings[abbreviation];

      const defaultCustomDisposition: SalesforceCallDisposition = {
        id: String(index),
        attributes: {
          name: defaultName,
          outcome: Outcome.NO_ANSWER,
        },
        type: 'callDisposition',
      };

      const customDispositionMap = callDispositions.filter((map) => map.attributes.name === mappingName);

      const { id, type, attributes } =
        customDispositionMap[customDispositionMap.length - 1] ?? defaultCustomDisposition;

      const transformed = {
        id,
        type,
        dispositionId,
        abbreviation,
        defaultAction,
        customName: attributes.name,
        defaultName,
        isCustom: isCustomDisposition(attributes.name ?? mappingName),
        outcome: attributes.outcome as Outcome,
      };

      transformedMappings.push(transformed);
    }

    return transformedMappings;
  }

  async function fetchAccounts(): Promise<SalesforceAccount[]> {
    setLoadingState({ ...loadingState, getAccounts: LOADING });
    const { data, error, success } = await getSalesforceAccountsAsync(userId, organizationId);

    if (data === undefined || success === false || error !== undefined) {
      setLoadingState({ ...loadingState, getAccounts: ERROR });

      toast({
        variant: 'destructive',
        title: 'No accounts linked!',
        description: 'You have to connect at least one Salesforce account to use this feature.',
      });

      setViewState({
        ...viewState,
        isSalesforceAccount: false,
      });

      setSalesforceAccounts([]);
      return [];
    }

    setViewState({ ...viewState, isSalesforceAccount: Boolean(data.length) });
    setSalesforceAccounts(data);
    setLoadingState({
      ...loadingState,
      getAccounts: SUCCESS,
    });
    return data;
  }

  const fetchMappings = async (): Promise<void> => {
    setLoadingState((prevState) => ({
      ...prevState,
      getMappings: LOADING,
    }));
    const { data } = await getSalesforceMapping(organizationId);

    const callDispositions = customDispositions.length > 0 ? customDispositions : await fetchCallDispositions();

    if (data === undefined) {
      setLoadingState({ ...loadingState, getMappings: ERROR });
      return;
    }

    const { mapping, id } = data;

    const transformedMapping: Disposition[] = transformMappings(
      JSON.parse(mapping) as DispositionRecord,
      callDispositions,
      id,
    );

    setMappings(transformedMapping);

    setLoadingState((prevState) => ({
      ...prevState,
      getMappings: SUCCESS,
    }));
  };

  async function fetchCallDispositions(): Promise<SalesforceCallDisposition[]> {
    setLoadingState((prevState) => ({
      ...prevState,
      getCallDispositions: LOADING,
    }));

    const nonce =
      salesforceAccounts[0]?.nonce ??
      (await fetchAccounts().then((accounts) => {
        return accounts[0]?.nonce;
      }));

    if (nonce === undefined) {
      return [];
    }

    const { data } = await getSalesforceDispositions(organizationId, nonce);

    if (data === undefined) {
      setLoadingState((prevState) => ({
        ...prevState,
        getCallDispositions: ERROR,
      }));

      toast({
        title: 'Error',
        description: 'Could not retrieve custom dispositions',
        variant: 'destructive',
      });

      setCustomDispositions([]);
      return [];
    }

    setCustomDispositions(data);

    setLoadingState((prevState) => ({
      ...prevState,
      getCallDispositions: SUCCESS,
    }));

    return data;
  }

  function selectNewDisposition(selected: Disposition, customDisposition: SalesforceCallDisposition): void {
    const {
      id,
      attributes: { name, outcome },
    } = customDisposition;

    const customSelected = customDispositions.find((d) => d.id === id);

    if (customSelected == null) {
      console.log('No custom disposition found');
      return;
    }
    setMappings((prev) => [
      ...prev.map((d) => {
        if (selected.abbreviation !== d.abbreviation) {
          return d;
        }
        mappingsHaveChanged.current = true;

        return {
          ...d,
          id,
          customName: name,
          isCustom: isCustomDisposition(name),
          outcome: outcome as Outcome,
        };
      }),
    ]);
  }

  async function saveNewDispositionMap(): Promise<void> {
    const dispositionId = mappings[0]?.dispositionId;

    setLoadingState({
      ...loadingState,
      saveMappings: LOADING,
    });

    const dispositionMap: DispositionMap = new Map();
    const patchMappings: SaveHubspotMappingDto = { mapping: '' };

    for (const disposition of mappings) {
      dispositionMap.set(DispositionAbbreviation[disposition.abbreviation], disposition.customName);
    }

    patchMappings.mapping = JSON.stringify(Object.fromEntries(dispositionMap));

    const { data, success } = await updateSalesforceMapping(dispositionId, patchMappings);

    if (data === undefined || success === false) {
      setLoadingState((prev) => ({
        ...prev,
        saveMappings: ERROR,
      }));

      mappingsHaveChanged.current = false;
      toast({
        title: 'Error',
        description: 'Could not save new disposition map',
        variant: 'destructive',
      });
    } else {
      setLoadingState({
        ...loadingState,
        saveMappings: SUCCESS,
      });

      mappingsHaveChanged.current = false;

      toast({
        title: 'Success',
        description: 'Your mapping was correctly saved.',
        variant: 'default',
      });
    }
  }

  useEffect(() => {
    const isTableLoading =
      loadingState.getAccounts === LOADING ||
      loadingState.getCallDispositions === LOADING ||
      loadingState.getMappings === LOADING;

    setViewState((prev) => ({
      ...prev,
      isTableLoading,
    }));
  }, [loadingState]);

  return {
    fetchMappings,
    fetchCallDispositions,
    fetchAccounts,
    selectNewDisposition,
    saveNewDispositionMap,
    mappings,
    customDispositions,
    salesforceAccounts,
    loadingState,
    viewState,
    mappingsHaveChanged,
  };
}
